/**
 * Voice Conversation Hook
 *
 * React hook that wraps both VoiceConversationEngine (pipeline mode) and
 * OpenAIRealtimeEngine (realtime mode) for component consumption.
 *
 * Pipeline mode: mic → Deepgram STT → transcript → RAG → ElevenLabs TTS
 * Realtime mode: mic → WebRTC → OpenAI Realtime API → WebRTC → speakers
 *
 * Responsibilities:
 *   - Engine lifecycle (create, configure, destroy per engine type)
 *   - Token/key acquisition (Deepgram ephemeral JWT or OpenAI ephemeral key)
 *   - Reactive state for UI binding (state, transcript, audioLevel, usage)
 *   - PTT hold/toggle detection (300ms threshold)
 *   - Open-mic mode activation/deactivation
 *   - Engine switching when settings change
 *   - Error handling and recovery
 *
 * Pipeline TTS bridge (parent component calls these in pipeline mode only):
 *   - notifyTTSStarted() when audio playback begins
 *   - notifyTTSFinished() when the reply's speech has ended, spoken or not
 *   - trackTTSCharacters() to accumulate cost data
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useSettingsStore } from '@/stores'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import { apiClient } from '@/services/apiClient'
import {
  VoiceConversationEngine,
  type VoiceEngineCallbacks,
  type VoiceEngineConfig,
} from '@/services/voice/VoiceConversationEngine'
import {
  OpenAIRealtimeEngine,
  type RealtimeEngineConfig,
  type RealtimeEngineCallbacks,
} from '@/services/voice/OpenAIRealtimeEngine'
import { localSttOptions, readOpenMicNoWordsSeconds } from '@/types/voice'
import type {
  VoiceConversationState,
  VoiceEngineType,
  TurnUsage,
} from '@/types/voice'
import type { EdgeLocalConfig } from '@/hooks/useEdgeLocalConfig'
import { resolveSttLanguage } from '@/utils/sttLanguageResolver'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseVoiceConversationOptions {
  /** Called with the final user transcript — typically wired to handleSendMessage */
  onFinalTranscript: (text: string) => void
  /** Called with the AI's complete response text (realtime mode only) */
  onAssistantTranscript?: (text: string) => void
  /** Called when an error occurs */
  onError?: (error: Error) => void
  /** Called when barge-in needs to interrupt TTS playback */
  onInterruptTTS?: () => void
  /** Edge-local config (when running on edge device with local speech services) */
  edgeLocalConfig?: EdgeLocalConfig | null
  /** Persona's STT language override (from persona.voiceConfig.sttLanguage) */
  personaSttLanguage?: string | null
  /** Conversation-level override (from VoiceControlBar local state) */
  conversationSttLanguageOverride?: string | null
}

export interface UseVoiceConversationReturn {
  /** Current engine state */
  state: VoiceConversationState
  /** Active engine type: 'pipeline' or 'openai-realtime' */
  engineType: VoiceEngineType
  /** Whether voice conversation feature is enabled in settings */
  isEnabled: boolean
  /** Whether the required credentials for the active engine are configured */
  hasCredentials: boolean
  /** Live partial transcript (updates in real-time) */
  partialTranscript: string
  /** Audio input level 0–1 for visualization */
  audioLevel: number
  /** Last completed turn's usage data */
  lastTurnUsage: TurnUsage | null
  /** Current error (cleared on next startListening) */
  error: Error | null

  // Open-mic state
  /** Whether open-mic mode is currently active */
  isOpenMicActive: boolean
  /** Whether VAD/server has detected active speech (open-mic only) */
  isSpeechDetected: boolean

  // PTT controls
  /** Start listening — fetches token and opens mic */
  startListening: () => Promise<void>
  /** Stop listening and finalize transcript */
  stopListening: () => Promise<void>
  /** Interrupt AI speech and return to idle */
  interrupt: () => void
  /** Cancel any ongoing operation */
  cancel: () => void

  // Open-mic controls
  /** Toggle open-mic mode on/off */
  toggleOpenMic: () => Promise<void>

  // TTS bridge — parent calls these to keep engine state in sync (pipeline only)
  /** Notify engine that TTS playback started */
  notifyTTSStarted: () => void
  /** Notify engine that a reply's speech ended, spoken or not; usage is emitted once per voice turn */
  notifyTTSFinished: () => void
  /** Track characters sent to TTS for cost estimation */
  trackTTSCharacters: (count: number) => void
  /** Report LLM usage from RAG server response */
  reportLLMUsage: (usage: {
    provider: string
    model: string
    promptTokens: number
    completionTokens: number
    totalTokens: number
    estimatedCost: number
  }) => void
  /** Resolved effective STT language after applying cascade (for display in VoiceControlBar) */
  effectiveSttLanguage: string
}

// PTT hold vs toggle threshold (ms)
const PTT_HOLD_THRESHOLD_MS = 300

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVoiceConversation(
  options: UseVoiceConversationOptions
): UseVoiceConversationReturn {
  const {
    onFinalTranscript,
    onAssistantTranscript,
    onError,
    onInterruptTTS,
    edgeLocalConfig,
    personaSttLanguage = null,
    conversationSttLanguageOverride = null,
  } = options

  // Settings
  const { voiceConversation, hasDeepgramKey, hasOpenAIKey, connections } =
    useSettingsStore()
  const ragProxyBase = useRagProxyBase()

  // Edge-local mode detection
  const isEdgeLocal = !!edgeLocalConfig?.stt

  // Resolved effective STT language (cascade: conversation override → persona → user → system default)
  const effectiveSttLanguage = useMemo(
    () =>
      resolveSttLanguage({
        conversationOverride: conversationSttLanguageOverride,
        personaSttLanguage,
        userSttLanguage: voiceConversation.sttLanguage,
      }),
    [conversationSttLanguageOverride, personaSttLanguage, voiceConversation.sttLanguage],
  )

  // Derive engine type and credentials from settings
  const engineType = voiceConversation.engine
  // In edge-local mode, no cloud credentials are needed
  const hasCredentials = isEdgeLocal
    ? true
    : engineType === 'pipeline'
      ? hasDeepgramKey
      : hasOpenAIKey

  // Reactive state
  const [state, setState] = useState<VoiceConversationState>('idle')
  const [partialTranscript, setPartialTranscript] = useState('')
  const [audioLevel, setAudioLevel] = useState(0)
  const [lastTurnUsage, setLastTurnUsage] = useState<TurnUsage | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isOpenMicActive, setIsOpenMicActive] = useState(false)
  const [isSpeechDetected, setIsSpeechDetected] = useState(false)

  // Engine refs (only one is active at a time)
  const pipelineEngineRef = useRef<VoiceConversationEngine | null>(null)
  const realtimeEngineRef = useRef<OpenAIRealtimeEngine | null>(null)
  const pttStartTimeRef = useRef(0)
  const isFetchingTokenRef = useRef(false)

  // Track engine type to detect changes
  const prevEngineTypeRef = useRef(engineType)

  // Stable callback refs (avoid re-creating engines on every render)
  const onFinalTranscriptRef = useRef(onFinalTranscript)
  const onAssistantTranscriptRef = useRef(onAssistantTranscript)
  const onErrorRef = useRef(onError)
  const onInterruptTTSRef = useRef(onInterruptTTS)

  useEffect(() => {
    onFinalTranscriptRef.current = onFinalTranscript
    onAssistantTranscriptRef.current = onAssistantTranscript
    onErrorRef.current = onError
    onInterruptTTSRef.current = onInterruptTTS
  }, [onFinalTranscript, onAssistantTranscript, onError, onInterruptTTS])

  // -----------------------------------------------------------------------
  // Engine type change detection — destroy old engines, reset state
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (voiceConversation.engine !== prevEngineTypeRef.current) {
      // Destroy both engines when switching types
      pipelineEngineRef.current?.destroy()
      pipelineEngineRef.current = null
      realtimeEngineRef.current?.destroy()
      realtimeEngineRef.current = null

      // Reset UI state
      setState('idle')
      setPartialTranscript('')
      setAudioLevel(0)
      setIsOpenMicActive(false)
      setIsSpeechDetected(false)
      setError(null)

      prevEngineTypeRef.current = voiceConversation.engine
    }
  }, [voiceConversation.engine])

  // -----------------------------------------------------------------------
  // Shared callbacks (used by both engine types)
  // -----------------------------------------------------------------------

  const sharedCallbacks: VoiceEngineCallbacks = {
    onStateChange: (newState) => setState(newState),
    onPartialTranscript: (text) => setPartialTranscript(text),
    onFinalTranscript: (text) => onFinalTranscriptRef.current(text),
    onAudioLevel: (level) => setAudioLevel(level),
    onTurnUsage: (usage) => setLastTurnUsage(usage),
    onError: (err) => {
      setError(err)
      onErrorRef.current?.(err)
    },
    onSpeechDetected: (detected) => setIsSpeechDetected(detected),
    onInterruptTTS: () => onInterruptTTSRef.current?.(),
  }

  // -----------------------------------------------------------------------
  // Pipeline engine lifecycle
  // -----------------------------------------------------------------------

  const getOrCreatePipelineEngine =
    useCallback((): VoiceConversationEngine => {
      if (pipelineEngineRef.current) return pipelineEngineRef.current

      const config: VoiceEngineConfig = {
        sttModel: isEdgeLocal
          ? (edgeLocalConfig?.stt?.model || 'base')
          : voiceConversation.models.sttModel,
        sttLanguage: isEdgeLocal
          ? (edgeLocalConfig?.stt?.language || 'en')
          : effectiveSttLanguage,
        autoSend: voiceConversation.autoSend,
        mode: voiceConversation.mode,
        openMicNoWordsMs: readOpenMicNoWordsSeconds(voiceConversation.openMicNoWordsSeconds) * 1000,
        // Edge-local: the person's thresholds for the local STT server (SA-1393)
        localStt: isEdgeLocal
          ? localSttOptions({
              localSilenceMs: voiceConversation.localSilenceMs,
              localSilenceThreshold: voiceConversation.localSilenceThreshold,
              localMaxBufferMs: voiceConversation.localMaxBufferMs,
            })
          : undefined,
        // Edge-local: use local Whisper STT provider
        ...(isEdgeLocal && edgeLocalConfig?.stt ? {
          sttProvider: 'whisper-local' as const,
          localSttWsUrl: `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${edgeLocalConfig.stt.wsPath}`,
        } : {}),
      }

      const engine = new VoiceConversationEngine(sharedCallbacks, config)
      pipelineEngineRef.current = engine
      return engine
    }, [
      isEdgeLocal,
      edgeLocalConfig,
      voiceConversation.models.sttModel,
      effectiveSttLanguage,
      voiceConversation.autoSend,
      voiceConversation.mode,
      voiceConversation.openMicNoWordsSeconds,
      voiceConversation.localSilenceMs,
      voiceConversation.localSilenceThreshold,
      voiceConversation.localMaxBufferMs,
      sharedCallbacks,
    ])

  // Update pipeline engine config when settings change
  useEffect(() => {
    pipelineEngineRef.current?.updateConfig({
      sttModel: isEdgeLocal
        ? (edgeLocalConfig?.stt?.model || 'base')
        : voiceConversation.models.sttModel,
      sttLanguage: isEdgeLocal
        ? (edgeLocalConfig?.stt?.language || 'en')
        : effectiveSttLanguage,
      autoSend: voiceConversation.autoSend,
      mode: voiceConversation.mode,
      openMicNoWordsMs: readOpenMicNoWordsSeconds(voiceConversation.openMicNoWordsSeconds) * 1000,
      localStt: isEdgeLocal
        ? localSttOptions({
            localSilenceMs: voiceConversation.localSilenceMs,
            localSilenceThreshold: voiceConversation.localSilenceThreshold,
            localMaxBufferMs: voiceConversation.localMaxBufferMs,
          })
        : undefined,
      ...(isEdgeLocal && edgeLocalConfig?.stt ? {
        sttProvider: 'whisper-local' as const,
      } : {}),
    })
  }, [
    isEdgeLocal,
    edgeLocalConfig,
    voiceConversation.models.sttModel,
    effectiveSttLanguage,
    voiceConversation.autoSend,
    voiceConversation.mode,
    voiceConversation.openMicNoWordsSeconds,
    voiceConversation.localSilenceMs,
    voiceConversation.localSilenceThreshold,
    voiceConversation.localMaxBufferMs,
  ])

  // -----------------------------------------------------------------------
  // Realtime engine lifecycle
  // -----------------------------------------------------------------------

  const getOrCreateRealtimeEngine =
    useCallback((): OpenAIRealtimeEngine => {
      if (realtimeEngineRef.current) return realtimeEngineRef.current

      const realtimeCallbacks: RealtimeEngineCallbacks = {
        ...sharedCallbacks,
        onAssistantTranscript: (text) => {
          onAssistantTranscriptRef.current?.(text)
        },
      }

      const config: RealtimeEngineConfig = {
        model: voiceConversation.models.realtimeModel,
        voice: voiceConversation.realtimeSettings.voice,
        turnDetection: voiceConversation.realtimeSettings.turnDetection,
        mode: voiceConversation.mode,
        ragServerUrl: ragProxyBase,
        ragApiKey: connections.ragApiKey,
      }

      const engine = new OpenAIRealtimeEngine(realtimeCallbacks, config)
      realtimeEngineRef.current = engine
      return engine
    }, [
      voiceConversation.models.realtimeModel,
      voiceConversation.realtimeSettings.voice,
      voiceConversation.realtimeSettings.turnDetection,
      voiceConversation.mode,
      ragProxyBase,
      connections.ragApiKey,
      sharedCallbacks,
    ])

  // Update realtime engine config when settings change
  useEffect(() => {
    realtimeEngineRef.current?.updateConfig({
      model: voiceConversation.models.realtimeModel,
      voice: voiceConversation.realtimeSettings.voice,
      turnDetection: voiceConversation.realtimeSettings.turnDetection,
      mode: voiceConversation.mode,
      ragServerUrl: ragProxyBase,
      ragApiKey: connections.ragApiKey,
    })
  }, [
    voiceConversation.models.realtimeModel,
    voiceConversation.realtimeSettings.voice,
    voiceConversation.realtimeSettings.turnDetection,
    voiceConversation.mode,
    ragProxyBase,
    connections.ragApiKey,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pipelineEngineRef.current?.destroy()
      pipelineEngineRef.current = null
      realtimeEngineRef.current?.destroy()
      realtimeEngineRef.current = null
    }
  }, [])

  // -----------------------------------------------------------------------
  // Token / key acquisition
  // -----------------------------------------------------------------------

  const fetchDeepgramToken = useCallback(async (): Promise<string> => {
    const response = await apiClient.fetch('/api/voice/deepgram-token', {
      method: 'POST',
      body: JSON.stringify({ ttl_secs: 30 }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.detail ||
          `Failed to get Deepgram token (${response.status})`
      )
    }

    const data = await response.json()
    return data.token
  }, [])

  const fetchEphemeralKey = useCallback(async (): Promise<string> => {
    const response = await apiClient.fetch(
      '/api/voice/openai-ephemeral-key',
      {
        method: 'POST',
        body: JSON.stringify({
          model: voiceConversation.models.realtimeModel,
          voice: voiceConversation.realtimeSettings.voice,
        }),
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.detail ||
          `Failed to get OpenAI ephemeral key (${response.status})`
      )
    }

    const data = await response.json()
    return data.token
  }, [
    voiceConversation.models.realtimeModel,
    voiceConversation.realtimeSettings.voice,
  ])

  // -----------------------------------------------------------------------
  // PTT controls
  // -----------------------------------------------------------------------

  const startListening = useCallback(async () => {
    if (isFetchingTokenRef.current) return

    setError(null)
    setPartialTranscript('')
    pttStartTimeRef.current = Date.now()

    if (engineType === 'openai-realtime') {
      // --- Realtime PTT ---
      const engine = getOrCreateRealtimeEngine()

      // Interrupt if AI is currently speaking
      if (engine.state === 'speaking') {
        engine.interrupt()
      }

      try {
        if (!engine.isSessionActive) {
          // First PTT press: establish WebRTC session
          isFetchingTokenRef.current = true
          const token = await fetchEphemeralKey()
          isFetchingTokenRef.current = false
          await engine.connect(token)
        }
        // Unmute mic and start capturing
        engine.startTalking()
      } catch (err) {
        isFetchingTokenRef.current = false
        const error = err instanceof Error ? err : new Error(String(err))
        setError(error)
        onErrorRef.current?.(error)
      }
    } else {
      // --- Pipeline PTT ---
      const engine = getOrCreatePipelineEngine()

      if (engine.state === 'speaking') {
        engine.interrupt()
      }

      try {
        // The engine begins the turn at the press and fetches the token itself.
        // Edge-local: no token needed, local STT server has no auth
        await engine.startListening(isEdgeLocal ? '' : fetchDeepgramToken)
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        setError(error)
        onErrorRef.current?.(error)
      }
    }
  }, [
    engineType,
    getOrCreateRealtimeEngine,
    getOrCreatePipelineEngine,
    fetchEphemeralKey,
    fetchDeepgramToken,
  ])

  const stopListening = useCallback(async () => {
    const holdDuration = Date.now() - pttStartTimeRef.current

    if (engineType === 'openai-realtime') {
      // --- Realtime PTT ---
      const engine = realtimeEngineRef.current
      if (!engine) return

      if (holdDuration >= PTT_HOLD_THRESHOLD_MS) {
        engine.stopTalking()
      }
      // Toggle mode: short click is a no-op on first release;
      // the user clicks again to stop (handled by component logic)
    } else {
      // --- Pipeline PTT ---
      const engine = pipelineEngineRef.current
      if (!engine) return

      if (holdDuration >= PTT_HOLD_THRESHOLD_MS) {
        await engine.stopAndFinalize()
      }
    }
  }, [engineType])

  const interrupt = useCallback(() => {
    if (engineType === 'openai-realtime') {
      realtimeEngineRef.current?.interrupt()
    } else {
      pipelineEngineRef.current?.interrupt()
    }
  }, [engineType])

  const cancel = useCallback(() => {
    if (engineType === 'openai-realtime') {
      realtimeEngineRef.current?.cancel()
    } else {
      pipelineEngineRef.current?.cancel()
    }
    setPartialTranscript('')
  }, [engineType])

  // -----------------------------------------------------------------------
  // Open-mic controls
  // -----------------------------------------------------------------------

  const toggleOpenMic = useCallback(async () => {
    if (engineType === 'openai-realtime') {
      // --- Realtime open-mic ---
      const existingEngine = realtimeEngineRef.current
      if (existingEngine?.isOpenMicActive) {
        await existingEngine.stopOpenMic()
        setIsOpenMicActive(false)
        setIsSpeechDetected(false)
        setPartialTranscript('')
      } else {
        const engine = getOrCreateRealtimeEngine()
        setError(null)
        setPartialTranscript('')
        try {
          await engine.startOpenMic(fetchEphemeralKey)
          setIsOpenMicActive(true)
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err))
          setError(error)
          onErrorRef.current?.(error)
        }
      }
    } else {
      // --- Pipeline open-mic ---
      const engine = getOrCreatePipelineEngine()
      if (engine.isOpenMicActive) {
        await engine.stopOpenMic()
        setIsOpenMicActive(false)
        setIsSpeechDetected(false)
        setPartialTranscript('')
      } else {
        setError(null)
        setPartialTranscript('')
        try {
          // Edge-local: token fetcher returns empty string (no auth needed)
          const tokenFetcher = isEdgeLocal
            ? async () => ''
            : fetchDeepgramToken
          await engine.startOpenMic(tokenFetcher)
          setIsOpenMicActive(true)
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err))
          setError(error)
          onErrorRef.current?.(error)
        }
      }
    }
  }, [
    engineType,
    isEdgeLocal,
    getOrCreateRealtimeEngine,
    getOrCreatePipelineEngine,
    fetchEphemeralKey,
    fetchDeepgramToken,
  ])

  // -----------------------------------------------------------------------
  // TTS bridge (pipeline mode only — realtime handles audio internally)
  // -----------------------------------------------------------------------

  const notifyTTSStarted = useCallback(() => {
    // Only meaningful for pipeline mode
    pipelineEngineRef.current?.notifyTTSStarted()
  }, [])

  const notifyTTSFinished = useCallback(() => {
    const engine = pipelineEngineRef.current
    if (!engine) return

    // Usage once per voice turn: only when the engine actually ended one
    if (engine.notifyTTSFinished()) engine.emitTurnUsage()
  }, [])

  const trackTTSCharacters = useCallback((count: number) => {
    pipelineEngineRef.current?.trackTTSCharacters(count)
  }, [])

  const reportLLMUsage = useCallback(
    (usage: {
      provider: string
      model: string
      promptTokens: number
      completionTokens: number
      totalTokens: number
      estimatedCost: number
    }) => {
      pipelineEngineRef.current?.reportLLMUsage(usage)
    },
    []
  )

  return {
    state,
    engineType,
    isEnabled: voiceConversation.enabled,
    hasCredentials,
    partialTranscript,
    audioLevel,
    lastTurnUsage,
    error,

    isOpenMicActive,
    isSpeechDetected,

    startListening,
    stopListening,
    interrupt,
    cancel,

    toggleOpenMic,

    notifyTTSStarted,
    notifyTTSFinished,
    trackTTSCharacters,
    reportLLMUsage,

    effectiveSttLanguage,
  }
}
